import { useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { MdClose, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { DarkModeContext } from "../Context/DarkModeContext.jsx";

export default function ManageVisibilityModal({
  open,
  count,
  itemLabel,
  onClose,
  onChange,
  loading = false,
}) {
  const { isDarkMode } = useContext(DarkModeContext);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !loading) onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [loading, onClose, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm overflow-y-auto"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) onClose();
      }}
    >
      <div
        className={`w-full max-w-lg overflow-y-auto rounded-3xl border shadow-2xl  max-h-[calc(100dvh-2rem)] ${
          isDarkMode
            ? "border-white/10 bg-[#191919] text-white"
            : "border-slate-200 bg-white text-slate-900"
        } `}
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-visibility-title"
      >
        <div className="flex items-start justify-between gap-4 px-6 pb-4 pt-6 sm:px-7 sm:pt-7">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-500">
              <MdVisibility size={25} aria-hidden="true" />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 id="manage-visibility-title" className="text-xl font-bold">
                  Manage visibility
                </h2>
                <span className="rounded-full bg-blue-500/10 px-2.5 py-1 text-xs font-bold text-blue-500">
                  {count} selected
                </span>
              </div>
              <p className={`mt-1 text-sm ${isDarkMode ? "text-gray-400" : "text-slate-500"}`}>
                Choose how students can access the selected {itemLabel}.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Close visibility modal"
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition disabled:opacity-50 ${
              isDarkMode ? "text-gray-400 hover:bg-white/10 hover:text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            <MdClose size={22} />
          </button>
        </div>

        <div className="grid gap-3 px-6 py-3 sm:grid-cols-2 sm:px-7">
          <button
            type="button"
            onClick={() => onChange(false)}
            disabled={loading}
            className={`group rounded-2xl border p-5 text-left transition disabled:cursor-wait disabled:opacity-60 ${
              isDarkMode
                ? "border-white/10 bg-white/[0.03] hover:border-emerald-500/60 hover:bg-emerald-500/10"
                : "border-slate-200 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50"
            }`}
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-500">
              <MdVisibility size={24} />
            </span>
            <span className="mt-4 block font-bold">Show to students</span>
            <span className={`mt-1 block text-sm leading-5 ${isDarkMode ? "text-gray-400" : "text-slate-500"}`}>
              Students will be able to find and open the selected {itemLabel}.
            </span>
          </button>

          <button
            type="button"
            onClick={() => onChange(true)}
            disabled={loading}
            className={`group rounded-2xl border p-5 text-left transition disabled:cursor-wait disabled:opacity-60 ${
              isDarkMode
                ? "border-white/10 bg-white/[0.03] hover:border-red-500/60 hover:bg-red-500/10"
                : "border-slate-200 bg-slate-50 hover:border-red-400 hover:bg-red-50"
            }`}
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/15 text-red-500">
              <MdVisibilityOff size={24} />
            </span>
            <span className="mt-4 block font-bold">Hide from students</span>
            <span className={`mt-1 block text-sm leading-5 ${isDarkMode ? "text-gray-400" : "text-slate-500"}`}>
              Students will no longer see the selected {itemLabel}.
            </span>
          </button>
        </div>

        <div className={`mt-4 flex items-center justify-between border-t px-6 py-4 sm:px-7 ${isDarkMode ? "border-white/10" : "border-slate-100"}`}>
          <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-slate-400"}`}>
            {loading ? "Updating visibility…" : "Changes apply immediately"}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${
              isDarkMode ? "bg-white/10 text-gray-200 hover:bg-white/15" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
